import { FC, useContext, useEffect } from 'react'

import { Icon } from '@joor/design-system'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { isAccountTypeBrand } from 'store/currentUser/selectors'

import Table from 'components/Core/Table/Table'
import { ImportOrdersModalIds } from 'modals/OrderList/OrderImportModal/orderImportModal.ids'
import { NEW_ORDER_REVIEW } from 'routes/paths'

import { ImportOrdersContext } from '../../ImportOrders.context'
import { ImportOrdersStandardContext } from '../../ImportOrdersStandard.context'
import {
  ButtonLink,
  Container,
  ResultCellContent,
  StyledTableRow,
  TableHeaderCell,
} from './ThirdStepStandard.styles'

// sc-247036: for multi-linesheet Excel Order files, show the file name and
// the specific linesheet/collection on their own lines instead of one long
// concatenated string, which was wrapping awkwardly.
const FileCellContent = ({
  filename,
  sheetName,
}: {
  filename: string
  sheetName?: string
}) => (
  <>
    <div>{`File: ${filename}`}</div>
    {sheetName && <div>{`Linesheet: ${sheetName}`}</div>}
  </>
)

const UnsuccessfulImportRow = ({
  filename,
  sheetName,
  error,
}: {
  filename: string
  sheetName?: string
  error: string
}) => (
  <StyledTableRow>
    <Table.Cell bgcolor="white">
      <FileCellContent filename={filename} sheetName={sheetName} />
    </Table.Cell>
    <Table.Cell bgcolor="white">
      <ResultCellContent>
        <Icon type="semantic" iconName="error" iconSize="small" plain />
        <span>{error}</span>
      </ResultCellContent>
    </Table.Cell>
    <Table.Cell bgcolor="white" />
  </StyledTableRow>
)

const SuccessfulImportRow = ({
  filename,
  sheetName,
  order,
}: {
  filename: string
  sheetName?: string
  order: { id: string; poNumber: string }
}) => {
  const { trackEvent } = useUserAnalytics()
  const isBrandAccount = useSelector(isAccountTypeBrand)
  return (
    <StyledTableRow>
      <Table.Cell bgcolor="white">
        <FileCellContent filename={filename} sheetName={sheetName} />
      </Table.Cell>
      <Table.Cell bgcolor="white">
        <ResultCellContent>
          <Icon type="semantic" iconName="success" iconSize="small" plain />
          <span>
            {isBrandAccount
              ? 'New Pending order created'
              : 'New order added to your cart'}
          </span>
        </ResultCellContent>
      </Table.Cell>
      <Table.Cell
        bgcolor={'white'}
        link={generatePath(NEW_ORDER_REVIEW, { id: order.id })}
      >
        <ButtonLink
          type="button"
          onClick={() =>
            trackEvent('Orders List_Import Orders Modal Click PO#')
          }
        >
          {order.poNumber}
        </ButtonLink>
      </Table.Cell>
    </StyledTableRow>
  )
}

// sc-247036: one Excel Order file can contain several worksheets (one per
// linesheet/collection). Renders one row per worksheet outcome instead of a
// single row for the whole file, so the brand can see exactly which
// linesheets were imported, and which failed, and why.
const renderSheetResultRows = (
  filename: string,
  sheetResults: Array<{
    sheetName: string
    status: 'created' | 'error'
    reason?: string
    orders: Array<{ id: string; poNumber: string }>
  }>,
) =>
  sheetResults.flatMap((sheetResult) => {
    const rowKey = `${filename}-${sheetResult.sheetName}`
    if (sheetResult.status === 'created') {
      return sheetResult.orders.map((order) => (
        <SuccessfulImportRow
          filename={filename}
          sheetName={sheetResult.sheetName}
          order={order}
          key={order.id}
        />
      ))
    }
    return [
      <UnsuccessfulImportRow
        filename={filename}
        sheetName={sheetResult.sheetName}
        error={sheetResult.reason || 'This linesheet could not be imported.'}
        key={rowKey}
      />,
    ]
  })

const ThirdStepStandard: FC = () => {
  const { importedOrders } = useContext(ImportOrdersStandardContext)
  const { setModalTitle } = useContext(ImportOrdersContext)

  useEffect(() => setModalTitle('Import Summary'), [setModalTitle])

  return (
    <Container
      id={ImportOrdersModalIds.ImportSummaryContainer}
      data-testid={ImportOrdersModalIds.ImportSummaryContainer}
    >
      <Table fullWidth>
        <Table.Header sticky>
          <StyledTableRow>
            <TableHeaderCell bgcolor="#CDD4DA" color="#FF0000">
              {'File'}
            </TableHeaderCell>
            <TableHeaderCell bgcolor="#CDD4DA">{'Result'}</TableHeaderCell>
            <TableHeaderCell bgcolor="#CDD4DA">{'PO#'}</TableHeaderCell>
          </StyledTableRow>
        </Table.Header>
        <Table.Body>
          {importedOrders.length &&
            importedOrders.map(({ error, filename, orders, sheetResults }) => {
              // sc-247036: when the backend reports a per-worksheet
              // breakdown, use it - it's the accurate picture for
              // multi-linesheet Excel Order files.
              if (sheetResults && sheetResults.length > 0) {
                return renderSheetResultRows(filename, sheetResults)
              }
              if (error) {
                return (
                  <UnsuccessfulImportRow
                    filename={filename}
                    error={error}
                    key={filename}
                  />
                )
              }
              return orders.map((order) => (
                <SuccessfulImportRow
                  filename={filename}
                  order={order}
                  key={order.id}
                />
              ))
            })}
        </Table.Body>
      </Table>
    </Container>
  )
}

export default ThirdStepStandard
